import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  PackageOpen,
  Plus,
  Minus,
  Search,
  Pencil,
  Trash2,
  X,
  Download,
  History,
  AlertTriangle,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { http, apiError } from "@/lib/api";
import AdminShell, {
  PageHead,
  useAdmin,
} from "@/components/AdminShell";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";
import MasterDataTransfer from "@/components/MasterDataTransfer";

const UNITS = [
  "pcs",
  "gram",
  "kg",
  "ml",
  "liter",
  "porsi",
  "pack",
  "botol",
  "cup",
];

const EMPTY_ITEM = {
  name: "",
  category: "Bahan",
  unit: "pcs",
  stock: "",
  min_stock: 5,
};

function fmt(value) {
  const n = Number(value || 0);

  if (Number.isInteger(n)) {
    return String(n);
  }

  return n.toLocaleString("id-ID", {
    maximumFractionDigits: 3,
  });
}

function InventoryInner() {
  const admin = useAdmin();
  const isOwner = admin?.user?.role === "owner";

  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("priority_name");
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null);
  const [adjusting, setAdjusting] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const [adjustForm, setAdjustForm] = useState({
    mode: "add",
    qty: "",
    reason: "Restock",
    note: "",
  });

  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  async function load() {
    try {
      const { data } = await http.get("/admin/inventory");
      setItems(data || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }

  async function loadMovements() {
    try {
      const { data } = await http.get(
        "/admin/inventory/movements",
        {
          params: {
            start,
            end,
          },
        },
      );

      setMovements(data || []);
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();

    const result = items.filter((item) => {
      if (!needle) return true;

      return `${item.name} ${item.category} ${item.unit}`
        .toLowerCase()
        .includes(needle);
    });

    const collator = new Intl.Collator("id-ID", {
      numeric: true,
      sensitivity: "base",
    });

    return [...result].sort((a, b) => {
      if (sortBy === "priority_name") {
        const priority = (item) => {
          if (item.out_of_stock) return 0;
          if (item.low_stock) return 1;
          return 2;
        };

        const priorityDiff =
          priority(a) - priority(b);

        if (priorityDiff !== 0) {
          return priorityDiff;
        }

        return collator.compare(
          String(a.name || ""),
          String(b.name || ""),
        );
      }

      if (sortBy === "stock_asc") {
        const stockDiff =
          Number(a.stock || 0) -
          Number(b.stock || 0);

        if (stockDiff !== 0) {
          return stockDiff;
        }

        return collator.compare(
          String(a.name || ""),
          String(b.name || ""),
        );
      }

      if (sortBy === "stock_desc") {
        const stockDiff =
          Number(b.stock || 0) -
          Number(a.stock || 0);

        if (stockDiff !== 0) {
          return stockDiff;
        }

        return collator.compare(
          String(a.name || ""),
          String(b.name || ""),
        );
      }

      if (sortBy === "name_asc") {
        return collator.compare(
          String(a.name || ""),
          String(b.name || ""),
        );
      }

      if (sortBy === "name_desc") {
        return collator.compare(
          String(b.name || ""),
          String(a.name || ""),
        );
      }

      if (sortBy === "low_first") {
        const aRatio =
          Number(a.min_stock || 0) > 0
            ? Number(a.stock || 0) /
              Number(a.min_stock || 1)
            : Number(a.stock || 0);

        const bRatio =
          Number(b.min_stock || 0) > 0
            ? Number(b.stock || 0) /
              Number(b.min_stock || 1)
            : Number(b.stock || 0);

        const ratioDiff = aRatio - bRatio;

        if (ratioDiff !== 0) {
          return ratioDiff;
        }

        return collator.compare(
          String(a.name || ""),
          String(b.name || ""),
        );
      }

      return collator.compare(
        String(a.name || ""),
        String(b.name || ""),
      );
    });
  }, [items, q, sortBy]);

  const stats = useMemo(
    () => ({
      total: items.length,
      low: items.filter(
        (item) =>
          item.low_stock &&
          !item.out_of_stock,
      ).length,
      out: items.filter(
        (item) => item.out_of_stock,
      ).length,
    }),
    [items],
  );

  async function saveItem() {
    if (!editing?.name?.trim()) {
      toast.error("Nama bahan wajib diisi.");
      return;
    }

    const body = {
      name: editing.name,
      category: editing.category || "Bahan",
      unit: editing.unit || "pcs",
      min_stock: Number(
        editing.min_stock || 0,
      ),
    };

    if (!editing.id) {
      body.stock = Number(editing.stock || 0);
    }

    try {
      if (editing.id) {
        await http.patch(
          `/admin/inventory/${editing.id}`,
          body,
        );
      } else {
        await http.post(
          "/admin/inventory",
          body,
        );
      }

      toast.success(
        editing.id
          ? "Bahan diperbarui"
          : "Bahan ditambahkan",
      );

      setEditing(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  function openAdjust(item, mode) {
    setAdjusting(item);

    setAdjustForm({
      mode,
      qty: "",
      reason:
        mode === "add"
          ? "Restock"
          : "Pemakaian Manual",
      note: "",
    });
  }

  async function adjustStock() {
    const qty = Math.abs(
      Number(adjustForm.qty || 0),
    );

    if (!qty) {
      toast.error(
        "Jumlah wajib lebih dari 0.",
      );
      return;
    }

    const delta =
      adjustForm.mode === "add"
        ? qty
        : -qty;

    try {
      await http.post(
        `/admin/inventory/${adjusting.id}/adjust`,
        {
          qty_delta: delta,
          reason: adjustForm.reason,
          note: adjustForm.note,
        },
      );

      toast.success(
        delta > 0
          ? "Stok ditambahkan"
          : "Stok dikurangi",
      );

      setAdjusting(null);
      await load();

      if (historyOpen) {
        await loadMovements();
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  function askDeleteItem(item) {
    setDeleteTarget(item);
  }

  async function confirmDeleteItem() {
    if (!deleteTarget) return;

    setDeleteBusy(true);

    try {
      await http.delete(
        `/admin/inventory/${deleteTarget.id}`,
      );

      toast.success("Bahan dihapus");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleteBusy(false);
    }
  }

  async function openHistory() {
    setHistoryOpen(true);
    await loadMovements();
  }

  async function downloadExcel() {
    try {
      const response = await http.get(
        "/admin/inventory/export",
        {
          params: {
            start,
            end,
          },
          responseType: "blob",
        },
      );

      const url = URL.createObjectURL(
        response.data,
      );

      const a =
        document.createElement("a");

      a.href = url;
      a.download =
        `laporan-stok-${start || "all"}_sd_${end || "all"}.xlsx`;

      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  return (
    <>
      <PageHead
        eyebrow="Inventory"
        title="Stok"
        desc="Bahan internal tidak muncul sebagai produk pelanggan. Produk dan topping mengambil stok dari sini."
      >
        <div className="flex flex-wrap items-center gap-2">
          {isOwner && (
            <MasterDataTransfer
              kind="inventory"
              label="Stok"
              onImported={load}
            />
          )}

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="rounded-xl border border-border bg-muted px-3 py-2.5 text-sm font-bold text-card-foreground outline-none"
            title="Urutkan stok"
            aria-label="Urutkan stok"
          >
            <option value="priority_name">Habis / Menipis → A–Z</option>
            <option value="name_asc">Nama A–Z / 0–9</option>
            <option value="name_desc">Nama Z–A / 9–0</option>
            <option value="stock_asc">Stok Tersedikit</option>
            <option value="stock_desc">Stok Terbanyak</option>
            <option value="low_first">Paling Kritis</option>
          </select>

          <button
            onClick={openHistory}
            className="flex items-center gap-2 rounded-xl bg-muted px-4 py-2.5 text-sm font-bold"
          >
            <History size={16} />
            Riwayat
          </button>

          {isOwner && (
            <button
              onClick={() =>
                setEditing({
                  ...EMPTY_ITEM,
                })
              }
              className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground"
            >
              <Plus size={16} />
              Bahan
            </button>
          )}
        </div>
      </PageHead>

      <div className="mb-4 grid grid-cols-3 gap-3">
        <Stat
          label="Total Bahan"
          value={stats.total}
          icon={<PackageOpen size={17} />}
        />

        <Stat
          label="Menipis"
          value={stats.low}
          icon={<AlertTriangle size={17} />}
          warn
        />

        <Stat
          label="Habis"
          value={stats.out}
          icon={<AlertTriangle size={17} />}
          danger
        />
      </div>

      <div className="mb-4 flex items-center gap-2 rounded-xl border border-border bg-card px-3">
        <Search
          size={16}
          className="text-muted-foreground"
        />

        <input
          value={q}
          onChange={(e) =>
            setQ(e.target.value)
          }
          placeholder="Cari bahan / kategori"
          className="w-full bg-transparent py-2.5 text-sm outline-none"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="wm-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((item) => (
            <div
              key={item.id}
              className="rounded-3xl border border-border bg-card p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-heading text-base font-extrabold text-card-foreground">
                    {item.name}
                  </div>

                  <div className="text-xs text-muted-foreground">
                    {item.category} · {item.unit}
                  </div>
                </div>

                <StockBadge item={item} />
              </div>

              <div className="mt-5">
                <div className="text-xs font-semibold text-muted-foreground">
                  Stok sekarang
                </div>

                <div className="mt-1 font-heading text-3xl font-extrabold text-card-foreground">
                  {fmt(item.stock)}{" "}
                  <span className="text-sm font-bold text-muted-foreground">
                    {item.unit}
                  </span>
                </div>

                <div className="mt-1 text-xs text-muted-foreground">
                  Minimum: {fmt(item.min_stock)}{" "}
                  {item.unit}
                </div>
              </div>

              {isOwner && (
                <>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button
                      onClick={() =>
                        openAdjust(
                          item,
                          "remove",
                        )
                      }
                      className="flex items-center justify-center gap-2 rounded-xl bg-destructive/10 py-2.5 text-xs font-bold text-destructive"
                    >
                      <Minus size={14} />
                      Kurangi
                    </button>

                    <button
                      onClick={() =>
                        openAdjust(
                          item,
                          "add",
                        )
                      }
                      className="flex items-center justify-center gap-2 rounded-xl bg-primary py-2.5 text-xs font-bold text-primary-foreground"
                    >
                      <Plus size={14} />
                      Tambah
                    </button>
                  </div>

                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button
                      onClick={() =>
                        setEditing({
                          ...item,
                        })
                      }
                      className="flex items-center justify-center gap-2 rounded-xl bg-muted py-2 text-xs font-bold"
                    >
                      <Pencil size={13} />
                      Edit
                    </button>

                    <button
                      onClick={() =>
                        askDeleteItem(item)
                      }
                      className="flex items-center justify-center gap-2 rounded-xl bg-muted py-2 text-xs font-bold text-destructive"
                    >
                      <Trash2 size={13} />
                      Hapus
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}

          {filtered.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              Belum ada bahan stok.
            </div>
          )}
        </div>
      )}

      {editing && (
        <Modal
          title={
            editing.id
              ? "Edit Bahan"
              : "Tambah Bahan"
          }
          onClose={() =>
            setEditing(null)
          }
        >
          <div className="space-y-3">
            <Field label="Nama Bahan">
              <input
                value={editing.name || ""}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    name: e.target.value,
                  })
                }
                className="input"
                placeholder="Contoh: Sayuran"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Kategori">
                <input
                  value={
                    editing.category || ""
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      category:
                        e.target.value,
                    })
                  }
                  className="input"
                  placeholder="Bahan / Kemasan"
                />
              </Field>

              <Field label="Satuan">
                <select
                  value={
                    editing.unit || "pcs"
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      unit: e.target.value,
                    })
                  }
                  className="input"
                >
                  {UNITS.map((unit) => (
                    <option
                      key={unit}
                      value={unit}
                    >
                      {unit}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            {!editing.id && (
              <Field label="Stok Awal">
                <input
                  type="number"
                  step="0.001"
                  min="0"
                  value={
                    editing.stock || ""
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      stock:
                        e.target.value,
                    })
                  }
                  className="input"
                />
              </Field>
            )}

            <Field label="Batas Minimum / Peringatan">
              <input
                type="number"
                step="0.001"
                min="0"
                value={
                  editing.min_stock || ""
                }
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    min_stock:
                      e.target.value,
                  })
                }
                className="input"
              />
            </Field>

            <button
              onClick={saveItem}
              className="w-full rounded-xl bg-primary py-3 text-sm font-bold text-primary-foreground"
            >
              Simpan
            </button>
          </div>
        </Modal>
      )}

      {adjusting && (
        <Modal
          title={`${adjustForm.mode === "add" ? "Tambah" : "Kurangi"} Stok — ${adjusting.name}`}
          onClose={() =>
            setAdjusting(null)
          }
        >
          <div className="space-y-3">
            <div className="rounded-2xl bg-muted p-3 text-sm">
              Stok sekarang:{" "}
              <b>
                {fmt(adjusting.stock)}{" "}
                {adjusting.unit}
              </b>
            </div>

            <Field
              label={`Jumlah (${adjusting.unit})`}
            >
              <input
                autoFocus
                type="number"
                step="0.001"
                min="0"
                value={adjustForm.qty}
                onChange={(e) =>
                  setAdjustForm({
                    ...adjustForm,
                    qty: e.target.value,
                  })
                }
                className="input"
              />
            </Field>

            <Field label="Alasan">
              <select
                value={adjustForm.reason}
                onChange={(e) =>
                  setAdjustForm({
                    ...adjustForm,
                    reason:
                      e.target.value,
                  })
                }
                className="input"
              >
                {(adjustForm.mode === "add"
                  ? [
                      "Pembelian",
                      "Restock",
                      "Koreksi Stok",
                      "Lainnya",
                    ]
                  : [
                      "Pemakaian Manual",
                      "Rusak / Waste",
                      "Expired",
                      "Koreksi Stok",
                      "Lainnya",
                    ]
                ).map((reason) => (
                  <option key={reason}>
                    {reason}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Catatan">
              <textarea
                rows={2}
                value={adjustForm.note}
                onChange={(e) =>
                  setAdjustForm({
                    ...adjustForm,
                    note: e.target.value,
                  })
                }
                className="input resize-none"
                placeholder="Opsional"
              />
            </Field>

            <button
              onClick={adjustStock}
              className={`w-full rounded-xl py-3 text-sm font-bold ${
                adjustForm.mode === "add"
                  ? "bg-primary text-primary-foreground"
                  : "bg-destructive text-destructive-foreground"
              }`}
            >
              {adjustForm.mode === "add"
                ? "Tambah Stok"
                : "Kurangi Stok"}
            </button>
          </div>
        </Modal>
      )}

      {historyOpen && (
        <Modal
          wide
          title="Riwayat Stok"
          onClose={() =>
            setHistoryOpen(false)
          }
        >
          <div className="mb-3 flex flex-wrap items-end gap-2">
            <Field label="Dari">
              <input
                type="date"
                value={start}
                onChange={(e) =>
                  setStart(e.target.value)
                }
                className="input"
              />
            </Field>

            <Field label="Sampai">
              <input
                type="date"
                value={end}
                onChange={(e) =>
                  setEnd(e.target.value)
                }
                className="input"
              />
            </Field>

            <button
              onClick={loadMovements}
              className="rounded-xl bg-muted px-4 py-2.5 text-xs font-bold"
            >
              Terapkan
            </button>

            <button
              onClick={downloadExcel}
              className="ml-auto flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-xs font-bold text-primary-foreground"
            >
              <Download size={14} />
              Excel
            </button>
          </div>

          <div className="max-h-[58dvh] overflow-auto rounded-2xl border border-border">
            <table className="w-full min-w-[760px] text-left text-xs">
              <thead className="sticky top-0 bg-muted">
                <tr>
                  {[
                    "Waktu",
                    "Bahan",
                    "Tipe",
                    "Perubahan",
                    "Sebelum",
                    "Sesudah",
                    "Order",
                    "Alasan",
                    "Admin",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-3 py-2.5 font-bold"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {movements.map((m) => (
                  <tr
                    key={m.id}
                    className="border-t border-border"
                  >
                    <td className="px-3 py-2">
                      {new Date(
                        m.created_at,
                      ).toLocaleString(
                        "id-ID",
                      )}
                    </td>

                    <td className="px-3 py-2 font-bold">
                      {m.item_name}
                    </td>

                    <td className="px-3 py-2">
                      {m.type}
                    </td>

                    <td
                      className={`px-3 py-2 font-bold ${
                        Number(
                          m.qty_delta,
                        ) >= 0
                          ? "text-emerald-500"
                          : "text-destructive"
                      }`}
                    >
                      {Number(
                        m.qty_delta,
                      ) > 0
                        ? "+"
                        : ""}
                      {fmt(m.qty_delta)}{" "}
                      {m.unit}
                    </td>

                    <td className="px-3 py-2">
                      {fmt(
                        m.stock_before,
                      )}
                    </td>

                    <td className="px-3 py-2">
                      {fmt(
                        m.stock_after,
                      )}
                    </td>

                    <td className="px-3 py-2">
                      {m.order_id || "-"}
                    </td>

                    <td className="px-3 py-2">
                      {m.reason || "-"}
                    </td>

                    <td className="px-3 py-2">
                      {m.admin_name ||
                        "System"}
                    </td>
                  </tr>
                ))}

                {movements.length ===
                  0 && (
                  <tr>
                    <td
                      colSpan="9"
                      className="p-8 text-center text-muted-foreground"
                    >
                      Belum ada pergerakan
                      stok.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      <ConfirmDeleteModal
        open={Boolean(deleteTarget)}
        title="Hapus Bahan Stok?"
        message="Bahan hanya dapat dihapus jika tidak lagi dipakai oleh produk atau modifier."
        itemName={deleteTarget?.name || ""}
        busy={deleteBusy}
        onCancel={() => !deleteBusy && setDeleteTarget(null)}
        onConfirm={confirmDeleteItem}
      />

      <style>{`
        .input {
          width: 100%;
          border: 1px solid hsl(var(--border));
          background: hsl(var(--muted));
          border-radius: .75rem;
          padding: .65rem .8rem;
          font-size: .85rem;
          outline: none;
          color: hsl(var(--card-foreground));
        }
      `}</style>
    </>
  );
}

export default function Inventory() {
  return (
    <AdminShell>
      <InventoryInner />
    </AdminShell>
  );
}

function StockBadge({ item }) {
  if (item.out_of_stock) {
    return (
      <span className="rounded-full bg-destructive/15 px-2.5 py-1 text-[10px] font-black text-destructive">
        HABIS
      </span>
    );
  }

  if (item.low_stock) {
    return (
      <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-[10px] font-black text-amber-500">
        MENIPIS
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2.5 py-1 text-[10px] font-black text-emerald-500">
      <CheckCircle2 size={11} />
      AMAN
    </span>
  );
}

function Stat({
  label,
  value,
  icon,
  warn,
  danger,
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div
        className={`grid h-8 w-8 place-items-center rounded-lg ${
          danger
            ? "bg-destructive/15 text-destructive"
            : warn
              ? "bg-amber-500/15 text-amber-500"
              : "bg-primary/15 text-primary"
        }`}
      >
        {icon}
      </div>

      <div className="mt-2 text-xs text-muted-foreground">
        {label}
      </div>

      <div className="font-heading text-xl font-extrabold">
        {value}
      </div>
    </div>
  );
}

function Modal({
  children,
  onClose,
  title,
  wide,
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div
        className={`relative max-h-[90dvh] w-full overflow-y-auto rounded-t-3xl border border-border bg-card p-6 sm:rounded-3xl ${
          wide
            ? "max-w-5xl"
            : "max-w-md"
        }`}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-xl font-extrabold text-card-foreground">
            {title}
          </h2>

          <button
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-full bg-muted"
          >
            <X size={18} />
          </button>
        </div>

        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold text-muted-foreground">
        {label}
      </span>

      {children}
    </label>
  );
}
